/*
 * TASKS PAGE
 *
 * Global task queue with board and list views backed by the persisted task domain.
 *
 * Recent changes:
 * - Replaced mock and local draft task data with API-backed task loading.
 * - Added manual task creation plus follow-up note and soft-delete actions.
 * - Added a deleted-tasks list while keeping active queues focused on non-deleted work.
 * - Added Kanban-style task drag-and-drop plus workflow label remapping.
 * - Reused the task editor in a right-side panel when opening tasks from the board and list views.
 * - Matched the edit task assignee field to the compact task panel control.
 * - Kept Kanban lanes as semantic headings and full-lane drop targets after the compact board refresh.
 * - Removed quick Complete, Snooze, and Cancel buttons from task cards and rows.
 * - Matched the task board to the established Kanban column, card, and drag-surface presentation.
 * - Made the four board lanes fill available width and reflow beside the task detail panel.
 * - Consolidated the page title, filters, view switch, and result count into one compact responsive toolbar.
 * - Vertically aligned lane labels and counts within fixed-height chevron headers.
 * - Fixed lane headers above independently scrolling card bodies.
 * - Optically centered each complete lane label/count row within its green header.
 * - Adopted the shared page layout so shell and page spacing are not duplicated.
 * - Removed toolbar padding so its top and bottom visual gaps match.
 * - Reuses the shared task composer for creation, editing, and read-only viewing.
 * - Makes Account, Contact, and Assignee filters searchable single-select controls.
 * - Removes the follow-up-note chat icon from Board task cards while retaining List Add Note.
 * - Highlights overdue Board card due values with the Dashboard's semantic rose text treatment.
 * - Labels the task filter as Status and removes Deleted Tasks and No Due Date choices.
 * - Adds a dedicated Canceled status filter for non-deleted canceled tasks.
 * - Keeps long Board and List task titles to one line with an ellipsis.
 * - Filters by one individual assignee across both single- and multi-assignee tasks.
 * - Labels the canceled task board lane as Cancelled.
 * - Adds the shared responsive page header, New task entry, filter recovery, compact summaries, and readable mobile List rows.
 * - Keeps mobile List field labels available to assistive technology.
 * - Centers the Tasks heading against the adjacent New task button.
 */

import { FC, useEffect, useMemo, useRef, useState, type DragEvent } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import { THEME_CLASSES } from '../constants/themeConstants';
import { EVENTS } from '../constants/eventConstants';
import { useEventBus } from '../context/EventBusContext';
import { useTeams } from '../context/TeamContext';
import { getTasks, TASKS_UPDATED_EVENT, updateTaskStatus } from '../services/taskService';
import { Task, TaskPriority, TaskStatus, canMutateTask, getTaskStatusLabel, isTaskClosed } from '../models/Task';
import Spinner from '../components/common/Spinner';
import PageLayout from '../components/patterns/PageLayout';
import TaskComposerDialog from '../components/task/TaskComposerDialog';
import { Button, SearchableSelect, Surface, type SearchableSelectOption } from '../components/primitives';

type QueueValue = 'myOpen' | 'allOpen' | 'waiting' | 'done' | 'canceled' | 'overdue' | 'today' | 'next7' | 'noDueDate' | 'all' | 'deleted';
type ViewValue = 'board' | 'list';

const BOARD_LANES: TaskStatus[] = ['Open', 'Waiting', 'Done', 'Canceled'];
const getBoardLaneLabel = (status: TaskStatus) => (
  status === 'Canceled' ? 'Cancelled' : getTaskStatusLabel(status)
);
const BOARD_DRAG_SCROLL_EDGE_PX = 96;
const BOARD_DRAG_SCROLL_MAX_STEP = 28;
const TASK_BOARD_HEADER_CLIP_PATHS = {
  first: 'polygon(0 0, calc(100% - 12px) 0, 100% 50%, calc(100% - 12px) 100%, 0 100%)',
  middle: 'polygon(0 0, calc(100% - 12px) 0, 100% 50%, calc(100% - 12px) 100%, 0 100%, 12px 50%)',
  last: 'polygon(0 0, 100% 0, 100% 100%, 0 100%, 12px 50%)'
};

const LANE_STYLES: Record<TaskStatus, { accent: string; badge: string }> = {
  Open: {
    accent: 'border-t-amber-400',
    badge: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200'
  },
  Waiting: {
    accent: 'border-t-sky-400',
    badge: 'bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-200'
  },
  Done: {
    accent: 'border-t-emerald-400',
    badge: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200'
  },
  Canceled: {
    accent: 'border-t-slate-400',
    badge: 'bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-200'
  }
};

const PRIORITY_STYLES: Record<TaskPriority, string> = {
  High: 'bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-200',
  Medium: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200',
  Low: 'bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-200'
};

const QUEUE_OPTIONS: Array<{ value: QueueValue; label: string }> = [
  { value: 'myOpen', label: 'My Todo Tasks' },
  { value: 'allOpen', label: 'All Active Tasks' },
  { value: 'waiting', label: 'In Progress' },
  { value: 'done', label: 'Completed' },
  { value: 'canceled', label: 'Canceled' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'today', label: 'Due Today' },
  { value: 'next7', label: 'Next 7 Days' },
  { value: 'all', label: 'All Tasks' }
];

const VIEW_OPTIONS: Array<{ value: ViewValue; label: string }> = [
  { value: 'board', label: 'Board' },
  { value: 'list', label: 'List' }
];

const formatDueDate = (dueDate: string | null) => {
  if (!dueDate) {
    return {
      label: 'No due date',
      tone: 'bg-slate-100 text-slate-700 dark:bg-slate-900 dark:text-slate-300',
      boardTextTone: THEME_CLASSES.text.secondary
    };
  }

  const parsedDueDate = new Date(`${dueDate}T00:00:00`);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  parsedDueDate.setHours(0, 0, 0, 0);

  const diffDays = Math.round((parsedDueDate.getTime() - today.getTime()) / 86400000);

  if (diffDays < 0) {
    return {
      label: `Overdue ${parsedDueDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`,
      tone: 'bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-200',
      boardTextTone: 'text-rose-600 dark:text-rose-300'
    };
  }

  if (diffDays === 0) {
    return {
      label: 'Due today',
      tone: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200',
      boardTextTone: THEME_CLASSES.text.secondary
    };
  }

  return {
    label: `Due ${parsedDueDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`,
    tone: 'bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-200',
    boardTextTone: THEME_CLASSES.text.secondary
  };
};

const formatTimestamp = (timestamp: string | null | undefined) => {
  if (!timestamp) {
    return 'Unknown';
  }

  return new Date(timestamp).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

const isDeletedTask = (task: Task) => Boolean(task.deletedAt);

const isOpenTask = (task: Task) => !isTaskClosed(task.status);

const matchesQueue = (task: Task, queue: QueueValue, currentUserId: number | null, today: Date) => {
  if (queue === 'deleted') {
    return isDeletedTask(task);
  }

  if (isDeletedTask(task)) {
    return false;
  }

  const dueDate = task.dueDate ? new Date(`${task.dueDate}T00:00:00`) : null;
  const diffDays = dueDate ? Math.round((dueDate.getTime() - today.getTime()) / 86400000) : null;
  const isAssignedToCurrentUser = currentUserId ? task.assigneeIds.includes(currentUserId) : false;

  switch (queue) {
    case 'myOpen':
      return isAssignedToCurrentUser && isOpenTask(task);
    case 'allOpen':
      return isOpenTask(task);
    case 'waiting':
      return task.status === 'Waiting';
    case 'done':
      return task.status === 'Done';
    case 'canceled':
      return task.status === 'Canceled';
    case 'overdue':
      return Boolean(diffDays !== null && diffDays < 0 && isOpenTask(task));
    case 'today':
      return diffDays === 0 && isOpenTask(task);
    case 'next7':
      return Boolean(diffDays !== null && diffDays >= 1 && diffDays <= 7 && isOpenTask(task));
    case 'noDueDate':
      return !task.dueDate && isOpenTask(task);
    case 'all':
    default:
      return true;
  }
};

const Tasks: FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUserId } = useTeams();
  const { publish } = useEventBus();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedView, setSelectedView] = useState<ViewValue>('board');
  const [selectedQueue, setSelectedQueue] = useState<QueueValue>('myOpen');
  const [selectedAccount, setSelectedAccount] = useState('all');
  const [selectedContact, setSelectedContact] = useState('all');
  const [selectedPriority, setSelectedPriority] = useState<'all' | TaskPriority>('all');
  const [selectedAssignee, setSelectedAssignee] = useState('all');
  const [isComposerOpen, setIsComposerOpen] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState<number | null>(null);
  const [draggingTaskId, setDraggingTaskId] = useState<number | null>(null);
  const [hoveredLane, setHoveredLane] = useState<TaskStatus | null>(null);
  const boardScrollRef = useRef<HTMLDivElement | null>(null);

  const matchesSecondaryFilters = (task: Task) => {
    const matchesAccount = selectedAccount === 'all' || task.accountName === selectedAccount;
    const matchesContact = selectedContact === 'all' || task.contactName === selectedContact;
    const matchesPriority = selectedPriority === 'all' || task.priority === selectedPriority;
    const matchesAssignee = selectedAssignee === 'all' || task.assigneeNames.includes(selectedAssignee);

    return matchesAccount && matchesContact && matchesPriority && matchesAssignee;
  };

  useEffect(() => {
    let isMounted = true;

    const loadTasks = async () => {
      setIsLoading(true);

      try {
        const loadedTasks = await getTasks({ includeDeleted: true });
        if (!isMounted) {
          return;
        }

        setTasks(loadedTasks);
        setError(null);
      } catch (loadError) {
        if (!isMounted) {
          return;
        }

        console.error('Failed to load tasks:', loadError);
        setTasks([]);
        setError('Unable to load tasks right now.');
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    void loadTasks();

    const handleTasksUpdated = () => {
      void loadTasks();
    };

    window.addEventListener(TASKS_UPDATED_EVENT, handleTasksUpdated);

    return () => {
      isMounted = false;
      window.removeEventListener(TASKS_UPDATED_EVENT, handleTasksUpdated);
    };
  }, []);

  const filterPool = useMemo(
    () => tasks.filter((task) => selectedQueue === 'deleted' ? isDeletedTask(task) : !isDeletedTask(task)),
    [tasks, selectedQueue]
  );

  const accountOptions = useMemo(
    () => ['all', ...Array.from(new Set(filterPool.map((task) => task.accountName))).sort((left, right) => left.localeCompare(right))],
    [filterPool]
  );

  const contactOptions = useMemo(
    () => [
      'all',
      ...Array.from(new Set(filterPool.map((task) => task.contactName).filter((contactName): contactName is string => Boolean(contactName))))
        .sort((left, right) => left.localeCompare(right))
    ],
    [filterPool]
  );

  const assigneeOptions = useMemo(
    () => [
      'all',
      ...Array.from(new Set(filterPool.flatMap((task) => task.assigneeNames)))
        .sort((left, right) => left.localeCompare(right))
    ],
    [filterPool]
  );

  const accountFilterOptions = useMemo<SearchableSelectOption[]>(
    () => accountOptions.map((accountName) => ({
      value: accountName,
      label: `Account: ${accountName === 'all' ? 'All accounts' : accountName}`,
    })),
    [accountOptions]
  );

  const contactFilterOptions = useMemo<SearchableSelectOption[]>(
    () => contactOptions.map((contactName) => ({
      value: contactName,
      label: `Contact: ${contactName === 'all' ? 'All contacts' : contactName}`,
    })),
    [contactOptions]
  );

  const assigneeFilterOptions = useMemo<SearchableSelectOption[]>(
    () => assigneeOptions.map((assigneeName) => ({
      value: assigneeName,
      label: `Assignee: ${assigneeName === 'all' ? 'All assignees' : assigneeName}`,
    })),
    [assigneeOptions]
  );

  const today = useMemo(() => {
    const value = new Date();
    value.setHours(0, 0, 0, 0);
    return value;
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('new') === '1') {
      setEditingTaskId(null);
      setIsComposerOpen(true);
    }
  }, [location.search]);

  const filteredTasks = useMemo(
    () => {
      const visibleTasks = tasks.filter((task) => {
        return matchesQueue(task, selectedQueue, currentUserId, today)
          && matchesSecondaryFilters(task);
      });

      if (selectedQueue === 'deleted') {
        visibleTasks.sort((left, right) =>
          new Date(right.deletedAt ?? 0).getTime() - new Date(left.deletedAt ?? 0).getTime()
        );
      }

      return visibleTasks;
    },
    [tasks, selectedAccount, selectedContact, selectedPriority, selectedAssignee, selectedQueue, currentUserId, today]
  );

  const hasActiveTaskFilters = selectedQueue !== 'all'
    || selectedAccount !== 'all'
    || selectedContact !== 'all'
    || selectedPriority !== 'all'
    || selectedAssignee !== 'all';
  const hasActiveTasks = tasks.some((task) => !isDeletedTask(task));

  const clearTaskFilters = () => {
    setSelectedQueue('all');
    setSelectedAccount('all');
    setSelectedContact('all');
    setSelectedPriority('all');
    setSelectedAssignee('all');
  };

  const openNewTask = () => {
    setEditingTaskId(null);
    setIsComposerOpen(true);
  };

  const editingTask = useMemo(
    () => editingTaskId === null ? null : tasks.find((task) => task.id === editingTaskId) ?? null,
    [editingTaskId, tasks]
  );

  const todoCount = filteredTasks.filter((task) => task.status === 'Open').length;
  const inProgressCount = filteredTasks.filter((task) => task.status === 'Waiting').length;
  const completedCount = filteredTasks.filter((task) => task.status === 'Done').length;
  const canceledCount = filteredTasks.filter((task) => task.status === 'Canceled').length;
  const deletedTodayCount = filteredTasks.filter((task) => {
    if (!task.deletedAt) {
      return false;
    }

    const deletedDate = new Date(task.deletedAt);
    deletedDate.setHours(0, 0, 0, 0);
    return deletedDate.getTime() === today.getTime();
  }).length;
  const deletedFromNotesCount = filteredTasks.filter((task) => task.sourceNoteId).length;

  const moveTaskToStatus = async (taskId: number, targetStatus: TaskStatus) => {
    const taskToMove = tasks.find((task) => task.id === taskId);
    if (!taskToMove || isDeletedTask(taskToMove) || taskToMove.status === targetStatus) {
      setDraggingTaskId(null);
      setHoveredLane(null);
      return;
    }

    if (!canMutateTask(taskToMove, currentUserId)) {
      setDraggingTaskId(null);
      setHoveredLane(null);
      toast.error('You can only move tasks you created or are assigned to.');
      return;
    }

    const previousTask = { ...taskToMove };

    setTasks((previousTasks) => previousTasks.map((task) => {
      if (task.id !== taskId) {
        return task;
      }

      return {
        ...task,
        status: targetStatus,
        completedAt: targetStatus === 'Done' ? task.completedAt ?? new Date().toISOString() : null,
        updatedAt: new Date().toISOString()
      };
    }));
    setDraggingTaskId(null);
    setHoveredLane(null);

    try {
      await updateTaskStatus(taskId, targetStatus);
      toast.success(`Task moved to ${getTaskStatusLabel(targetStatus)}.`);
    } catch (actionError) {
      console.error('Failed to update task status from drag and drop:', actionError);
      setTasks((previousTasks) => previousTasks.map((task) => task.id === taskId ? previousTask : task));
      toast.error('Unable to move this task right now.');
    }
  };

  const scrollBoardDuringDrag = (event: DragEvent<HTMLElement>) => {
    const boardElement = boardScrollRef.current;

    if (!boardElement || draggingTaskId === null) {
      return;
    }

    const boardRect = boardElement.getBoundingClientRect();
    if (boardRect.height <= 0) {
      return;
    }

    const pointerY = event.clientY;
    const pointerX = event.clientX;
    const topEdge = boardRect.top + BOARD_DRAG_SCROLL_EDGE_PX;
    const bottomEdge = boardRect.bottom - BOARD_DRAG_SCROLL_EDGE_PX;
    const leftEdge = boardRect.left + BOARD_DRAG_SCROLL_EDGE_PX;
    const rightEdge = boardRect.right - BOARD_DRAG_SCROLL_EDGE_PX;
    let verticalScrollDelta = 0;
    let horizontalScrollDelta = 0;

    if (pointerY < topEdge) {
      const intensity = Math.min(1, (topEdge - pointerY) / BOARD_DRAG_SCROLL_EDGE_PX);
      verticalScrollDelta = -Math.ceil(intensity * BOARD_DRAG_SCROLL_MAX_STEP);
    } else if (pointerY > bottomEdge) {
      const intensity = Math.min(1, (pointerY - bottomEdge) / BOARD_DRAG_SCROLL_EDGE_PX);
      verticalScrollDelta = Math.ceil(intensity * BOARD_DRAG_SCROLL_MAX_STEP);
    }

    if (pointerX < leftEdge) {
      const intensity = Math.min(1, (leftEdge - pointerX) / BOARD_DRAG_SCROLL_EDGE_PX);
      horizontalScrollDelta = -Math.ceil(intensity * BOARD_DRAG_SCROLL_MAX_STEP);
    } else if (pointerX > rightEdge) {
      const intensity = Math.min(1, (pointerX - rightEdge) / BOARD_DRAG_SCROLL_EDGE_PX);
      horizontalScrollDelta = Math.ceil(intensity * BOARD_DRAG_SCROLL_MAX_STEP);
    }

    if (verticalScrollDelta !== 0) {
      boardElement.scrollTop += verticalScrollDelta;
    }

    if (horizontalScrollDelta !== 0) {
      boardElement.scrollLeft += horizontalScrollDelta;
    }
  };

  const getDroppedTaskId = (event: DragEvent<HTMLElement>) => {
    if (draggingTaskId !== null) {
      return draggingTaskId;
    }

    const transferredTaskValue = event.dataTransfer.getData('text/plain');
    if (!transferredTaskValue) {
      return null;
    }

    const transferredTaskId = Number(transferredTaskValue);
    return Number.isFinite(transferredTaskId) ? transferredTaskId : null;
  };

  const handleTaskDragStart = (event: DragEvent<HTMLElement>, taskId: number) => {
    event.stopPropagation();
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', String(taskId));
    setDraggingTaskId(taskId);
  };

  const handleTaskDragEnd = () => {
    setDraggingTaskId(null);
    setHoveredLane(null);
  };

  const handleBoardDragOver = (event: DragEvent<HTMLDivElement>) => {
    if (draggingTaskId === null) {
      return;
    }

    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    scrollBoardDuringDrag(event);
  };

  const handleStatusDragOver = (event: DragEvent<HTMLElement>, lane: TaskStatus) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    scrollBoardDuringDrag(event);
    setHoveredLane(lane);
  };

  const handleStatusDragLeave = (lane: TaskStatus) => {
    setHoveredLane((currentLane) => currentLane === lane ? null : currentLane);
  };

  const handleStatusDrop = (event: DragEvent<HTMLElement>, lane: TaskStatus) => {
    event.preventDefault();

    const taskId = getDroppedTaskId(event);
    if (taskId === null) {
      setHoveredLane(null);
      return;
    }

    void moveTaskToStatus(taskId, lane);
  };

  const handleAddFollowUpNote = (task: Task) => {
    publish(EVENTS.OPEN_NOTE_MODAL, {
      accountId: task.accountId,
      contactId: task.contactId ?? undefined,
    });
  };

  const handleOpenTaskEditor = (task: Task) => {
    setIsComposerOpen(false);
    setEditingTaskId(task.id);
  };

  const handleCloseTaskDialog = () => {
    setIsComposerOpen(false);
    setEditingTaskId(null);

    const params = new URLSearchParams(location.search);
    if (params.get('new') === '1') {
      params.delete('new');
      const search = params.toString();
      navigate({ pathname: location.pathname, search: search ? `?${search}` : '' }, { replace: true });
    }
  };

  const handleTaskSaved = (task: Task) => {
    setTasks((previousTasks) => {
      const taskIndex = previousTasks.findIndex((currentTask) => currentTask.id === task.id);
      if (taskIndex === -1) {
        return [task, ...previousTasks];
      }

      return previousTasks.map((currentTask) => currentTask.id === task.id ? task : currentTask);
    });
  };

  const activeTaskView = selectedView === 'board' ? (
    <div
      ref={boardScrollRef}
      role="region"
      aria-label="Task status board"
      onDragOver={handleBoardDragOver}
      className={`min-h-0 flex-1 overflow-x-auto overflow-y-hidden rounded-xl border ${THEME_CLASSES.card.border} bg-white shadow-lg dark:bg-gray-800`}
    >
      <div
        data-testid="task-board-lanes"
        className="grid h-full min-h-0 min-w-[48rem] w-full grid-cols-4 items-stretch pb-6"
      >
        {BOARD_LANES.map((lane, laneIndex) => {
          const laneTasks = filteredTasks.filter((task) => task.status === lane);

          return (
            <TaskKanbanLane
              key={lane}
              lane={lane}
              tasks={laneTasks}
              isFirst={laneIndex === 0}
              isLast={laneIndex === BOARD_LANES.length - 1}
              isActiveDropTarget={hoveredLane === lane}
              draggingTaskId={draggingTaskId}
              currentUserId={currentUserId}
              onOpenTask={handleOpenTaskEditor}
              onDragStart={handleTaskDragStart}
              onDragEnd={handleTaskDragEnd}
              onDragOver={handleStatusDragOver}
              onDragLeave={handleStatusDragLeave}
              onDrop={handleStatusDrop}
            />
          );
        })}
      </div>
    </div>
  ) : (
    <TaskListView
      tasks={filteredTasks}
      currentUserId={currentUserId}
      onAddFollowUpNote={handleAddFollowUpNote}
      onOpenTask={handleOpenTaskEditor}
    />
  );

  return (
    <PageLayout className="px-4">
      <header
        data-testid="tasks-toolbar"
      >
        <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:gap-4">
          <div className="flex shrink-0 items-center gap-3">
            <h1 className={`m-0 flex h-9 items-center text-xl font-semibold ${THEME_CLASSES.text.primary}`}>
              Tasks
            </h1>
            <Button
              type="button"
              onClick={openNewTask}
              size="sm"
              className="min-h-9"
              aria-label="New task"
              leftIcon={(
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M10 3a1 1 0 0 1 1 1v5h5a1 1 0 1 1 0 2h-5v5a1 1 0 1 1-2 0v-5H4a1 1 0 1 1 0-2h5V4a1 1 0 0 1 1-1z" clipRule="evenodd" />
                </svg>
              )}
            >
              <span className="xl:hidden 2xl:inline">New task</span>
            </Button>
          </div>

          <div className="grid min-w-0 flex-1 grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
            <div className="min-w-0">
              <label className="sr-only" htmlFor="tasks-queue-filter">
                Status
              </label>
              <select
                id="tasks-queue-filter"
                value={selectedQueue}
                onChange={(event) => setSelectedQueue(event.target.value as QueueValue)}
                className={`h-9 w-full min-w-0 rounded-lg border ${THEME_CLASSES.input.border} ${THEME_CLASSES.input.background} ${THEME_CLASSES.input.text} px-3 py-1.5 text-sm shadow-sm`}
              >
                {QUEUE_OPTIONS.map((queue) => (
                  <option key={queue.value} value={queue.value}>
                    Status: {queue.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="min-w-0">
              <label className="sr-only" htmlFor="tasks-account-filter">
                Account
              </label>
              <SearchableSelect
                id="tasks-account-filter"
                label="Account"
                value={selectedAccount}
                onChange={setSelectedAccount}
                options={accountFilterOptions}
                searchPlaceholder="Search accounts..."
              />
            </div>

            <div className="min-w-0">
              <label className="sr-only" htmlFor="tasks-contact-filter">
                Contact
              </label>
              <SearchableSelect
                id="tasks-contact-filter"
                label="Contact"
                value={selectedContact}
                onChange={setSelectedContact}
                options={contactFilterOptions}
                searchPlaceholder="Search contacts..."
              />
            </div>

            <div className="min-w-0">
              <label className="sr-only" htmlFor="tasks-priority-filter">
                Priority
              </label>
              <select
                id="tasks-priority-filter"
                value={selectedPriority}
                onChange={(event) => setSelectedPriority(event.target.value as 'all' | TaskPriority)}
                className={`h-9 w-full min-w-0 rounded-lg border ${THEME_CLASSES.input.border} ${THEME_CLASSES.input.background} ${THEME_CLASSES.input.text} px-3 py-1.5 text-sm shadow-sm`}
              >
                <option value="all">Priority: All priorities</option>
                <option value="High">Priority: High</option>
                <option value="Medium">Priority: Medium</option>
                <option value="Low">Priority: Low</option>
              </select>
            </div>

            <div className="min-w-0">
              <label className="sr-only" htmlFor="tasks-assignee-filter">
                Assignee
              </label>
              <SearchableSelect
                id="tasks-assignee-filter"
                label="Assignee"
                value={selectedAssignee}
                onChange={setSelectedAssignee}
                options={assigneeFilterOptions}
                searchPlaceholder="Search assignees..."
              />
            </div>
          </div>

          <div className="flex shrink-0 flex-wrap items-center gap-2">
            <div className={`inline-flex h-9 rounded-xl border ${THEME_CLASSES.card.border} ${THEME_CLASSES.card.background} p-1 shadow-sm`}>
              {VIEW_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setSelectedView(option.value)}
                  aria-pressed={selectedView === option.value}
                  className={`rounded-lg px-3 py-1 text-sm font-medium transition-colors ${selectedView === option.value
                    ? `${THEME_CLASSES.button.primary} shadow-sm`
                    : `${THEME_CLASSES.text.secondary} hover:bg-slate-100 dark:hover:bg-slate-900`
                    }`}
                >
                  {option.label}
                </button>
              ))}
            </div>

            <div className={`flex h-9 items-center whitespace-nowrap rounded-xl border ${THEME_CLASSES.card.border} ${THEME_CLASSES.card.background} px-3 text-sm ${THEME_CLASSES.text.secondary} shadow-sm`}>
              {filteredTasks.length} of {tasks.length} tasks shown
            </div>
          </div>
        </div>
      </header>

      {(selectedQueue === 'deleted' || selectedView === 'list') && (
        <div className={`grid grid-cols-2 gap-3 md:gap-4 ${selectedQueue === 'deleted' ? 'md:grid-cols-3' : 'md:grid-cols-4'}`}>
          {selectedQueue === 'deleted' ? (
            <>
              <SummaryCard label="Deleted Tasks" value={filteredTasks.length} tone="rose" />
              <SummaryCard label="Deleted Today" value={deletedTodayCount} tone="sky" />
              <SummaryCard label="Deleted Note Tasks" value={deletedFromNotesCount} tone="amber" />
            </>
          ) : (
            <>
              <SummaryCard
                label="Todo"
                value={todoCount}
                tone="amber"
                dropStatus="Open"
                canDrop={draggingTaskId !== null}
                isActiveDropTarget={hoveredLane === 'Open'}
                onDragOver={handleStatusDragOver}
                onDragLeave={handleStatusDragLeave}
                onDrop={handleStatusDrop}
              />
              <SummaryCard
                label="In Progress"
                value={inProgressCount}
                tone="sky"
                dropStatus="Waiting"
                canDrop={draggingTaskId !== null}
                isActiveDropTarget={hoveredLane === 'Waiting'}
                onDragOver={handleStatusDragOver}
                onDragLeave={handleStatusDragLeave}
                onDrop={handleStatusDrop}
              />
              <SummaryCard
                label="Completed"
                value={completedCount}
                tone="emerald"
                dropStatus="Done"
                canDrop={draggingTaskId !== null}
                isActiveDropTarget={hoveredLane === 'Done'}
                onDragOver={handleStatusDragOver}
                onDragLeave={handleStatusDragLeave}
                onDrop={handleStatusDrop}
              />
              <SummaryCard
                label="Cancelled"
                value={canceledCount}
                tone="slate"
                dropStatus="Canceled"
                canDrop={draggingTaskId !== null}
                isActiveDropTarget={hoveredLane === 'Canceled'}
                onDragOver={handleStatusDragOver}
                onDragLeave={handleStatusDragLeave}
                onDrop={handleStatusDrop}
              />
            </>
          )}
        </div>
      )}

      {isLoading ? (
        <div className="flex flex-1 items-center justify-center">
          <Spinner />
        </div>
      ) : error ? (
        <div className={`rounded-3xl border ${THEME_CLASSES.card.border} ${THEME_CLASSES.card.background} px-6 py-12 text-center shadow-sm`}>
          <h2 className={`text-lg font-semibold ${THEME_CLASSES.text.primary}`}>Unable to load tasks</h2>
          <p className={`mt-3 text-sm ${THEME_CLASSES.text.secondary}`}>{error}</p>
        </div>
      ) : selectedQueue === 'deleted' ? (
        <DeletedTaskListView tasks={filteredTasks} />
      ) : editingTask ? (
        <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_28rem]">
          <div className="flex min-h-0 min-w-0 overflow-hidden">
            {activeTaskView}
          </div>
          <TaskComposerDialog
            open
            currentUserId={currentUserId}
            task={editingTask}
            onTaskSaved={handleTaskSaved}
            onClose={handleCloseTaskDialog}
            presentation="panel"
          />
        </div>
      ) : filteredTasks.length === 0 ? (
        <Surface variant="elevated" className="mx-auto w-full max-w-xl px-6 py-12 text-center" id="tasks-empty-state">
          <h2 className={`text-lg font-semibold ${THEME_CLASSES.text.primary}`}>
            {!hasActiveTasks ? 'No tasks yet' : 'No tasks match your filters'}
          </h2>
          <p className={`mt-3 text-sm ${THEME_CLASSES.text.secondary}`}>
            {!hasActiveTasks
              ? 'Create a task to keep follow-up work visible and organized.'
              : hasActiveTaskFilters
                ? 'Clear the active filters to see all current tasks.'
                : 'Try again after more tasks are available.'}
          </p>
          <div className="mt-5">
            <Button onClick={!hasActiveTasks ? openNewTask : clearTaskFilters} size="sm">
              {!hasActiveTasks ? 'New task' : 'Clear filters'}
            </Button>
          </div>
        </Surface>
      ) : (
        activeTaskView
      )}

      <TaskComposerDialog
        open={isComposerOpen}
        currentUserId={currentUserId}
        task={null}
        onTaskSaved={handleTaskSaved}
        onClose={handleCloseTaskDialog}
      />
    </PageLayout>
  );
};

const SummaryCard: FC<{
  label: string;
  value: number;
  tone: 'amber' | 'sky' | 'rose' | 'slate' | 'emerald';
  dropStatus?: TaskStatus;
  canDrop?: boolean;
  isActiveDropTarget?: boolean;
  onDragOver?: (event: DragEvent<HTMLDivElement>, status: TaskStatus) => void;
  onDragLeave?: (status: TaskStatus) => void;
  onDrop?: (event: DragEvent<HTMLDivElement>, status: TaskStatus) => void;
}> = ({ label, value, tone, dropStatus, canDrop = false, isActiveDropTarget = false, onDragOver, onDragLeave, onDrop }) => {
  const tones = {
    amber: 'from-amber-100 to-amber-50 text-amber-900 dark:from-amber-950 dark:to-slate-950 dark:text-amber-200',
    sky: 'from-sky-100 to-sky-50 text-sky-900 dark:from-sky-950 dark:to-slate-950 dark:text-sky-200',
    rose: 'from-rose-100 to-rose-50 text-rose-900 dark:from-rose-950 dark:to-slate-950 dark:text-rose-200',
    emerald: 'from-emerald-100 to-emerald-50 text-emerald-900 dark:from-emerald-950 dark:to-slate-950 dark:text-emerald-200',
    slate: 'from-slate-200 to-slate-50 text-slate-900 dark:from-slate-900 dark:to-slate-950 dark:text-slate-200'
  };

  return (
    <div
      role={dropStatus ? 'group' : undefined}
      aria-label={dropStatus ? `Drop tasks to ${label}` : `${label} summary`}
      onDragOver={dropStatus && canDrop ? (event) => onDragOver?.(event, dropStatus) : undefined}
      onDragLeave={dropStatus && canDrop ? () => onDragLeave?.(dropStatus) : undefined}
      onDrop={dropStatus && canDrop ? (event) => onDrop?.(event, dropStatus) : undefined}
      className={`rounded-3xl border ${THEME_CLASSES.card.border} bg-gradient-to-br ${tones[tone]} px-4 py-3 shadow-sm transition-shadow md:px-5 md:py-4 ${isActiveDropTarget ? 'ring-2 ring-sky-300 dark:ring-sky-800' : canDrop ? 'ring-1 ring-slate-300 dark:ring-slate-700' : ''}`}
    >
      <div className="text-xs font-semibold uppercase tracking-[0.18em] opacity-70">{label}</div>
      <div className="mt-1 text-2xl font-semibold md:mt-3 md:text-3xl">{value}</div>
    </div>
  );
};

const TaskKanbanLane: FC<{
  lane: TaskStatus;
  tasks: Task[];
  isFirst: boolean;
  isLast: boolean;
  isActiveDropTarget: boolean;
  draggingTaskId: number | null;
  currentUserId: number | null;
  onOpenTask: (task: Task) => void;
  onDragStart: (event: DragEvent<HTMLElement>, taskId: number) => void;
  onDragEnd: () => void;
  onDragOver: (event: DragEvent<HTMLElement>, status: TaskStatus) => void;
  onDragLeave: (status: TaskStatus) => void;
  onDrop: (event: DragEvent<HTMLElement>, status: TaskStatus) => void;
}> = ({
  lane,
  tasks,
  isFirst,
  isLast,
  isActiveDropTarget,
  draggingTaskId,
  currentUserId,
  onOpenTask,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDragLeave,
  onDrop
}) => {
  const headerClipPath = isFirst
    ? TASK_BOARD_HEADER_CLIP_PATHS.first
    : isLast
      ? TASK_BOARD_HEADER_CLIP_PATHS.last
      : TASK_BOARD_HEADER_CLIP_PATHS.middle;

  return (
    <section
      role="region"
      aria-label={`${getBoardLaneLabel(lane)} task lane`}
      className="flex h-full min-h-0 min-w-0 flex-col"
    >
      <div className="relative flex-shrink-0">
        <div
          className="relative flex h-11 items-center bg-green-200 px-6 text-sm font-semibold text-green-800 dark:bg-green-800 dark:text-green-50"
          style={{ clipPath: headerClipPath }}
        >
          <div className="flex w-full translate-y-1 items-baseline justify-between">
            <h2 className="min-w-0 truncate leading-5">{getBoardLaneLabel(lane)}</h2>
            <span className="ml-2 shrink-0 font-bold leading-5">{tasks.length}</span>
          </div>
        </div>
      </div>

      <div
        data-task-lane-dropzone={lane}
        onDragOver={(event) => onDragOver(event, lane)}
        onDragLeave={() => onDragLeave(lane)}
        onDrop={(event) => onDrop(event, lane)}
        className={`min-h-0 flex-1 space-y-4 overflow-y-auto bg-gray-50 p-4 transition-all duration-200 dark:bg-gray-900 ${isActiveDropTarget
          ? 'border-2 border-dashed border-green-500 bg-green-50 dark:bg-green-900/10'
          : 'border border-gray-200 dark:border-gray-700'
          }`}
      >
        {tasks.map((task) => (
          <TaskCard
            key={task.id}
            task={task}
            canEditTask={canMutateTask(task, currentUserId)}
            isDragging={draggingTaskId === task.id}
            onOpenTask={onOpenTask}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
          />
        ))}
        {tasks.length === 0 && (
          <div className={`flex h-40 items-center justify-center rounded-lg border border-dashed ${THEME_CLASSES.card.border} px-4 text-center text-sm ${THEME_CLASSES.text.secondary}`}>
            {isActiveDropTarget && draggingTaskId !== null
              ? `Drop to move this task to ${getBoardLaneLabel(lane)}.`
              : `No tasks match this queue in ${getBoardLaneLabel(lane).toLowerCase()}.`}
          </div>
        )}
      </div>
    </section>
  );
};

const TaskCard: FC<{
  task: Task;
  canEditTask: boolean;
  isDragging: boolean;
  onOpenTask: (task: Task) => void;
  onDragStart: (event: DragEvent<HTMLElement>, taskId: number) => void;
  onDragEnd: () => void;
}> = ({
  task,
  canEditTask,
  isDragging,
  onOpenTask,
  onDragStart,
  onDragEnd
}) => {
  const dueDate = formatDueDate(task.dueDate);
  const isDeleted = Boolean(task.deletedAt);
  const showTaskActions = canEditTask && !isDeleted;

  return (
    <div
      draggable={showTaskActions}
      onDragStart={(event) => {
        if (!showTaskActions) {
          event.preventDefault();
          return;
        }

        onDragStart(event, task.id);
      }}
      onDragEnd={onDragEnd}
      className={`transition-all duration-150 ${isDragging ? 'opacity-50' : ''} ${showTaskActions ? 'cursor-grab active:cursor-grabbing' : ''}`}
    >
      <article
        onDoubleClick={() => onOpenTask(task)}
        className={`${THEME_CLASSES.card.background} ${THEME_CLASSES.card.hover.full} rounded-xl border ${THEME_CLASSES.card.border} p-4 shadow-lg ${showTaskActions ? '' : 'cursor-pointer'}`}
      >
        <div className="mb-3">
          <button
            type="button"
            draggable={showTaskActions}
            onClick={() => onOpenTask(task)}
            className={`block min-w-0 w-full truncate text-left text-sm font-semibold leading-5 ${THEME_CLASSES.text.primary} hover:underline`}
          >
            {task.title}
          </button>
        </div>

        <dl className="mb-3 flex-1 space-y-2 text-xs">
          <div className="flex items-start justify-between gap-2">
            <dt className={`${THEME_CLASSES.text.secondary} shrink-0`}>Account:</dt>
            <dd className={`${THEME_CLASSES.text.secondary} text-right`}>{task.accountName}</dd>
          </div>
          <div className="flex items-start justify-between gap-2">
            <dt className={`${THEME_CLASSES.text.secondary} shrink-0`}>Assignee:</dt>
            <dd className={`${THEME_CLASSES.text.secondary} text-right`}>{task.assigneeName}</dd>
          </div>
          {task.contactName && (
            <div className="flex items-start justify-between gap-2">
              <dt className={`${THEME_CLASSES.text.secondary} shrink-0`}>Contact:</dt>
              <dd className={`${THEME_CLASSES.text.secondary} text-right`}>{task.contactName}</dd>
            </div>
          )}
          <div className="flex items-start justify-between gap-2">
            <dt className={`${THEME_CLASSES.text.secondary} shrink-0`}>Due:</dt>
            <dd className={`${dueDate.boardTextTone} text-right`}>{dueDate.label}</dd>
          </div>
        </dl>

        <div className={`flex items-center justify-between border-t ${THEME_CLASSES.card.border} pt-2`}>
          <div className="flex min-w-0 flex-wrap items-center gap-1">
            <span className={`rounded-full px-2 py-0.5 text-xs ${PRIORITY_STYLES[task.priority]}`}>
              {task.priority}
            </span>
            <span className="max-w-[7rem] truncate rounded-full bg-sky-100 px-2 py-0.5 text-xs text-sky-800 dark:bg-sky-950 dark:text-sky-200">
              {task.sourceLabel}
            </span>
            {!canEditTask && (
              <span className="rounded-full bg-slate-200 px-2 py-0.5 text-xs text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                View only
              </span>
            )}
          </div>
        </div>
      </article>
    </div>
  );
};

const TaskListView: FC<{
  tasks: Task[];
  currentUserId: number | null;
  onAddFollowUpNote: (task: Task) => void;
  onOpenTask: (task: Task) => void;
}> = ({ tasks, currentUserId, onAddFollowUpNote, onOpenTask }) => {
  return (
    <div className={`min-h-0 flex-1 overflow-hidden rounded-3xl border ${THEME_CLASSES.card.border} ${THEME_CLASSES.card.background} shadow-sm`}>
      <div className={`hidden grid-cols-[minmax(0,2.3fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,0.9fr)_minmax(0,0.9fr)] items-center gap-4 border-b ${THEME_CLASSES.card.border} px-5 py-4 text-center text-[11px] font-semibold uppercase tracking-[0.16em] ${THEME_CLASSES.text.secondary} md:grid`}>
        <div>Task</div>
        <div>Status</div>
        <div>Priority</div>
        <div>Assignee</div>
        <div>Account</div>
        <div>Contact</div>
        <div>Due date</div>
        <div>Actions</div>
      </div>

      <div className="max-h-full overflow-y-auto">
        {tasks.length > 0 ? (
          tasks.map((task) => (
            <TaskListRow
              key={task.id}
              task={task}
              canEditTask={canMutateTask(task, currentUserId)}
              onAddFollowUpNote={onAddFollowUpNote}
              onOpenTask={onOpenTask}
            />
          ))
        ) : (
          <div className={`flex h-56 items-center justify-center px-6 text-center text-sm ${THEME_CLASSES.text.secondary}`}>
            No tasks match the current filters.
          </div>
        )}
      </div>
    </div>
  );
};

const DeletedTaskListView: FC<{
  tasks: Task[];
}> = ({ tasks }) => {
  return (
    <div className={`min-h-0 flex-1 overflow-hidden rounded-3xl border ${THEME_CLASSES.card.border} ${THEME_CLASSES.card.background} shadow-sm`}>
      <div className={`grid grid-cols-[minmax(0,2fr)_minmax(0,1.1fr)_minmax(0,1.1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,0.9fr)] items-center gap-4 border-b ${THEME_CLASSES.card.border} px-5 py-4 text-center text-[11px] font-semibold uppercase tracking-[0.16em] ${THEME_CLASSES.text.secondary}`}>
        <div>Task</div>
        <div>Deleted By</div>
        <div>Deleted At</div>
        <div>Account</div>
        <div>Contact</div>
        <div>Source</div>
      </div>

      <div className="max-h-full overflow-y-auto">
        {tasks.length > 0 ? (
          tasks.map((task) => (
            <div key={task.id} className={`grid grid-cols-[minmax(0,2fr)_minmax(0,1.1fr)_minmax(0,1.1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,0.9fr)] items-center gap-4 border-b ${THEME_CLASSES.card.border} px-5 py-4 text-center text-sm`}>
              <div className="min-w-0 text-left">
                <div className={`truncate font-semibold ${THEME_CLASSES.text.primary}`}>{task.title}</div>
                <div className={`mt-1 text-xs ${THEME_CLASSES.text.secondary}`}>{getTaskStatusLabel(task.status)}</div>
              </div>
              <div className={`min-w-0 truncate ${THEME_CLASSES.text.primary}`}>{task.deletedByUserName || 'Unknown user'}</div>
              <div className={`min-w-0 ${THEME_CLASSES.text.secondary}`}>{formatTimestamp(task.deletedAt)}</div>
              <div className={`min-w-0 truncate ${THEME_CLASSES.text.primary}`}>{task.accountName}</div>
              <div className={`min-w-0 truncate ${THEME_CLASSES.text.secondary}`}>{task.contactName || '-'}</div>
              <div className={`min-w-0 truncate ${THEME_CLASSES.text.secondary}`}>{task.sourceLabel}</div>
            </div>
          ))
        ) : (
          <div className={`flex h-56 items-center justify-center px-6 text-center text-sm ${THEME_CLASSES.text.secondary}`}>
            No deleted tasks match the current filters.
          </div>
        )}
      </div>
    </div>
  );
};

const TaskListRow: FC<{
  task: Task;
  canEditTask: boolean;
  onAddFollowUpNote: (task: Task) => void;
  onOpenTask: (task: Task) => void;
}> = ({ task, canEditTask, onAddFollowUpNote, onOpenTask }) => {
  const dueDate = formatDueDate(task.dueDate);

  const stopRowAction = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
  };

  return (
    <div
      onDoubleClick={() => onOpenTask(task)}
      className={`grid grid-cols-2 gap-3 border-b ${THEME_CLASSES.card.border} p-4 text-sm md:grid-cols-[minmax(0,2.3fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,0.9fr)_minmax(0,0.9fr)] md:items-center md:gap-4 md:px-5 md:py-4 md:text-center ${canEditTask ? 'cursor-pointer' : ''}`}
    >
      <div className="col-span-2 min-w-0 text-left md:col-span-1">
        <button
          type="button"
          onClick={() => onOpenTask(task)}
          className={`block min-w-0 w-full truncate text-left font-semibold ${THEME_CLASSES.text.primary} hover:underline`}
        >
          {task.title}
        </button>
        <div className={`mt-1 text-xs ${THEME_CLASSES.text.secondary}`}>{task.sourceLabel}</div>
      </div>

      <div className="min-w-0 text-left md:text-center">
        <span className={`mb-1 block text-[11px] font-semibold uppercase tracking-wide md:hidden ${THEME_CLASSES.text.secondary}`}>Status</span>
        <span className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold ${LANE_STYLES[task.status].badge}`}>
          {getTaskStatusLabel(task.status)}
        </span>
      </div>

      <div className="min-w-0 text-left md:text-center">
        <span className={`mb-1 block text-[11px] font-semibold uppercase tracking-wide md:hidden ${THEME_CLASSES.text.secondary}`}>Priority</span>
        <span className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold ${PRIORITY_STYLES[task.priority]}`}>
          {task.priority}
        </span>
      </div>

      <div className="min-w-0 text-left md:text-center">
        <span className={`mb-1 block text-[11px] font-semibold uppercase tracking-wide md:hidden ${THEME_CLASSES.text.secondary}`}>Assignee</span>
        <span className={`block truncate ${THEME_CLASSES.text.primary}`}>{task.assigneeName}</span>
      </div>
      <div className="min-w-0 text-left md:text-center">
        <span className={`mb-1 block text-[11px] font-semibold uppercase tracking-wide md:hidden ${THEME_CLASSES.text.secondary}`}>Account</span>
        <span className={`block truncate ${THEME_CLASSES.text.primary}`}>{task.accountName}</span>
      </div>
      <div className="min-w-0 text-left md:text-center">
        <span className={`mb-1 block text-[11px] font-semibold uppercase tracking-wide md:hidden ${THEME_CLASSES.text.secondary}`}>Contact</span>
        <span className={`block truncate ${THEME_CLASSES.text.secondary}`}>{task.contactName || '-'}</span>
      </div>
      <div className="min-w-0 text-left md:text-center">
        <span className={`mb-1 block text-[11px] font-semibold uppercase tracking-wide md:hidden ${THEME_CLASSES.text.secondary}`}>Due date</span>
        <span className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold ${dueDate.tone}`}>
          {dueDate.label}
        </span>
      </div>

      <div className="col-span-2 flex flex-wrap items-end justify-start gap-2 md:col-span-1 md:justify-center">
        <span className={`w-full text-[11px] font-semibold uppercase tracking-wide md:hidden ${THEME_CLASSES.text.secondary}`}>Actions</span>
        <button
          type="button"
          onClick={(event) => {
            stopRowAction(event);
            onAddFollowUpNote(task);
          }}
          className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-800 dark:text-slate-300 dark:hover:bg-slate-900"
        >
          Add Note
        </button>
        {!canEditTask && (
          <span className={`inline-flex items-center rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium ${THEME_CLASSES.text.secondary}`}>
            View only
          </span>
        )}
      </div>
    </div>
  );
};

export default Tasks;
